import React, { useEffect, useState } from 'react';

const About: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const element = document.getElementById('about');
    if (element) {
      observer.observe(element);
    }

    return () => observer.disconnect();
  }, []);

  const skills = [
    { name: 'HTML', category: 'language' },
    { name: 'PHP', category: 'language' },
    { name: 'CSS', category: 'language' },
    { name: 'Tailwind', category: 'library' },
    { name: 'Sass', category: 'library' },
    { name: 'JavaScript', category: 'language' },
    { name: 'JQuery', category: 'library' },
    { name: 'React', category: 'library' },
    { name: 'Node.js', category: 'library' },
    { name: 'Typescript', category: 'library' },
    { name: 'Python', category: 'language' },
    { name: 'Git', category: 'version-control' },
    { name: 'Github', category: 'version-control' },
    { name: 'AEM', category: 'cms' },
    { name: 'Wordpress', category: 'cms' },
    { name: 'Squarespace', category: 'cms' },
    { name: 'Wix', category: 'cms' },
    { name: 'Sanity', category: 'cms' },
    { name: 'mySQL', category: 'database' },
    { name: 'Artificial Intelligence', category: 'ai' },
    { name: 'AI Prompting', category: 'ai' },
    { name: 'Prompt Engineering', category: 'ai' },
  ];

  const skillCategories = {
    language: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    library: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    'version-control': 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
    cms: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
    database: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    ai: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200',
  };

  return (
    <section id="about" className="section-padding">
      <div className="container-custom">
        <div className="text-center mb-16">
          <h2 className={`text-4xl md:text-5xl font-bold mb-4 transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            <span className="text-white">About Me</span>
          </h2>
          <p className={`text-xl text-dark-300 max-w-3xl mx-auto transition-all duration-1000 delay-200 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            <i>"The best way to predict the future is to invent it."</i> <br />
            <span className="text-primary-400 font-semibold">- Alan Kay</span>
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-12 items-start">
          {/* Image Section */}
          <div className={`lg:w-1/2 transition-all duration-1000 delay-300 mx-[auto] my-0 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            <div className="relative">
              <img
                src="/assets/png/richard-in-vegas.png"
                alt="Richard in Vegas"
                className="w-full object-cover rounded-2xl shadow-2xl"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-2xl"></div>
            </div>                        
          </div>

          {/* Content Section - Get to know me and Skills stacked */}
          <div className="lg:w-1/2 space-y-8">
            {/* Get to know me section */}
            <div className={`space-y-4 transition-all duration-1000 delay-300 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}>
              <h3 className="text-2xl font-bold text-white">
                Get to know me!
              </h3>
              <div className="prose prose-lg prose-invert max-w-none">
                <p className="text-dark-300 leading-relaxed">
                  I'm Richard J. Baird, a Full-Stack Developer based in Atlanta, GA. I am a technical leader who believes the strongest solutions are built by the strongest teasm. My background inclueds building web apps and websites that are fast, accessible, and easy to maintain. I've done work relating to healthcare, automotive, finance, travel, CPG, and eCommerce, using both custom code and CMS platforms. 
                </p><br />
                <p className="text-dark-300 leading-relaxed">
                  Beyond writing code, I've taken on responsibilities as a lead and mentor, guiding teams through complex projects, setting standards for best practices, and helping junior developers grow. I value being a collaborative leader who ensures projects meet both technical and business goals, while fostering an environment where others can learn and succeed.                
                </p><br />
                <p className="text-dark-300 leading-relaxed">
                  My toolkit includes JavaScript (React, jQuery, Node.js), CSS (Sass, Tailwind), Python, SQL, and AI prompting and prompt engineering, which I use to streamline workflows and solve problems in creative ways.
                </p><br />
                <p className="text-dark-300 leading-relaxed">
                  I'm always learning and looking for ways to sharpen my skills — whether that's through new frameworks, tools, or techniques. Outside of work, I like traveling with my fiancée, gaming, and staying active. Relaxing is also part of the process!
                </p><br />
                <p className="text-dark-300 leading-relaxed font-semibold">
                  Don't hesitate to reach out - we can probably develop something together!
                </p>
              </div>
              <button
              onClick={() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })}
              className="btn-primary mt-6"
            >
              Contact
            </button>
            </div>

            {/* Skills section */}
            <div className={`space-y-6 transition-all duration-1000 delay-500 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}>
              <h3 className="text-2xl font-bold text-white">
                My Skills
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {skills.map((skill, index) => (
                  <div
                    key={skill.name}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 transform hover:scale-105 ${
                      skillCategories[skill.category as keyof typeof skillCategories]
                    }`}
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    {skill.name}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
